import { FC, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Link, useTheme } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ActionButton } from './action-button';
import { LoadingState } from './loading-state';
import { useAutofixStyles } from './styles';
import {
  useApi,
  useAnalytics,
  identityApiRef,
  githubAuthApiRef,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import ReactMarkdown from 'react-markdown';
import { AutoFixConfig, AutofixStepResult } from '@internal/yardstick-common';
import { AutofixV2Step, AutoFixV2StepExecutionResponse } from './types';
import { yardstickApiRef } from '../../../api';
import { extractGitHubResourceDetails } from './analytics-utils';

interface AutofixV2SimpleFlowProps {
  issueId: string;
  autofixStep: AutoFixConfig;
  remediationSteps?: string;
  onClose?: () => void;
  entityRef?: string;
  checkId?: string;
  isGroupedIssue?: boolean; // Flag for grouped issues in logical accordion
  forceExpandPreview?: boolean; // Force preview to be expanded (no collapse option)
  issueIds?: string[]; // NEW: Array of issue IDs for bulk execution (resource-grouped)
  _isResourceGrouped?: boolean; // NEW: Flag indicating resource-grouped execution (prefixed with _ as intentionally unused for now)
}

interface FlowState {
  stepResult: AutofixV2Step | null;
  executing: boolean;
  loading: boolean;
  error: string | null;
  fetchError: string | null;
  previewExpanded: boolean;
}

/**
 * Pattern 2: Simple AutoFix (Single Step)
 *
 * This component handles a straightforward 2-phase flow:
 * 1. Apply autofix directly (no AI recommendation)
 * 2. Show success result
 */
export const AutofixV2SingleAutoFixFlow: FC<AutofixV2SimpleFlowProps> = ({
  issueId,
  autofixStep,
  remediationSteps: _remediationSteps, // Not used - we now use autofixStep.params.body for preview
  onClose,
  entityRef,
  checkId,
  isGroupedIssue: _isGroupedIssue = false, // Unused: for future feature flag
  forceExpandPreview = false,
  issueIds,
  _isResourceGrouped = false, // Unused: for future resource-grouped UI features
}) => {
  // Void unused variable to satisfy TypeScript
  void _isResourceGrouped;

  const classes = useAutofixStyles();
  const yardstickApi = useApi(yardstickApiRef);
  const analytics = useAnalytics();
  const identityApi = useApi(identityApiRef);
  const theme = useTheme();
  const githubAuthApi = useApi(githubAuthApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);

  const [state, setState] = useState<FlowState>({
    stepResult: null,
    executing: false,
    loading: true,
    error: null,
    fetchError: null,
    previewExpanded: forceExpandPreview, // Start expanded if forced
  });

  // Get user information for analytics
  const getUserInfo = useCallback(async (): Promise<string> => {
    try {
      const { displayName, email } = await identityApi.getProfileInfo();
      return displayName || email?.replace(/@.*/, '') || 'unknown';
    } catch {
      return 'unknown';
    }
  }, [identityApi]);

  // Determine the action type from handler
  const isGitHubIssue = autofixStep.handler.includes('github-issue');
  const isPullRequest = autofixStep.handler.includes('pull-request');
  const isEndorAction = autofixStep.handler.includes('endor-action');
  const copilotRequested =
    Array.isArray(autofixStep.params?.assignees) &&
    autofixStep.params!.assignees.includes('copilot-swe-agent[bot]');

  // Get action-specific text
  const getActionText = () => {
    if (isGitHubIssue)
      return {
        verb: 'Create',
        noun: 'GitHub Issue',
        pastTense: 'GitHub Issue Created',
        actionRequired:
          'Review the issue and assign it to GitHub Copilot to generate a pull request with the fix.',
      };
    if (isPullRequest)
      return {
        verb: 'Create',
        noun: 'Pull Request',
        pastTense: 'Pull Request Created',
        actionRequired: 'Review and merge the pull request to apply the fix.',
      };
    if (isEndorAction)
      return {
        verb: 'Apply',
        noun: 'Endor Action',
        pastTense: 'Endor Action Applied',
        actionRequired:
          'Review the changes and take any necessary follow-up actions.',
      };
    return {
      verb: 'Apply',
      noun: 'Auto-Remediation',
      pastTense: 'Auto-Remediation Applied',
      actionRequired:
        'Review the changes and take any necessary follow-up actions.',
    };
  };

  const actionText = getActionText();

  // Fetch existing step result on mount
  const fetchStepResult = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, fetchError: null }));
      const steps: AutofixStepResult[] =
        await yardstickApi.getAutofixV2StepResults(issueId);
      const result = steps.find(
        (s: AutofixStepResult) => s.stepConfig?.handler === autofixStep.handler,
      );

      // Map AutofixStepResult to AutofixV2Step
      const mapToV2Step = (
        stepResult: AutofixStepResult | undefined,
      ): AutofixV2Step | null => {
        if (!stepResult) return null;

        let status: 'success' | 'failed' | 'pending';
        if (stepResult.status === 'success') {
          status = 'success';
        } else if (stepResult.status === 'failed') {
          status = 'failed';
        } else {
          status = 'pending';
        }

        return {
          id: `${stepResult.issueId}-${stepResult.stepName}`,
          stepName: stepResult.stepName,
          stepConfig: stepResult.stepConfig,
          stepInputParams: stepResult.stepInputParams,
          stepOutputParams: stepResult.stepOutputParams,
          autofixText: stepResult.autofixText,
          error_message: stepResult.errorMessage,
          status,
          createdAt:
            stepResult.createdAt instanceof Date
              ? stepResult.createdAt.toISOString()
              : stepResult.createdAt,
          updatedAt:
            stepResult.updatedAt instanceof Date
              ? stepResult.updatedAt.toISOString()
              : stepResult.updatedAt,
        };
      };

      setState(prev => ({
        ...prev,
        loading: false,
        fetchError: null,
        stepResult: mapToV2Step(result),
      }));
    } catch (error) {
      // Error fetching step result
      setState(prev => ({
        ...prev,
        loading: false,
        fetchError:
          error instanceof Error
            ? error.message
            : 'Failed to load autofix status',
      }));
    }
  }, [issueId, autofixStep.handler, yardstickApi]);

  // Execute the autofix step
  const executeStep = useCallback(async () => {
    // Compute retry status from state
    const isRetry = !!(
      state.stepResult && state.stepResult.status === 'failed'
    );

    // Determine step type for analytics
    let stepType: string;
    let userToken: string | undefined;
    if (autofixStep.handler.includes('github-issue')) {
      stepType = 'github_issue';
    } else if (autofixStep.handler.includes('pull-request')) {
      stepType = 'github_pull_request';
    } else if (autofixStep.handler.includes('endor-action')) {
      stepType = 'endor-action';
    } else {
      stepType = 'auto_remediation';
    }

    // Track analytics before execution
    try {
      const user = await getUserInfo();

      analytics.captureEvent(
        'insights:auto_remediation:user_action',
        'auto_remediation',
        {
          attributes: {
            entity: entityRef || 'unknown',
            checkId: checkId || 'unknown',
            issueId: issueId || 'unknown',
            actionType: isRetry ? 'apply_retry' : 'apply',
            stepType,
            isRetry,
            user,
          },
        },
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('failed to capture analytic event:', error);
    }
    try {
      setState(prev => ({ ...prev, executing: true, error: null }));

      if (stepType === 'github_issue') {
        // For GitHub issue creation, we may need to pass a user token based on feature flag and frontend input
        userToken = await githubAuthApi.getAccessToken([
          'read:user',
          'read:org',
          'repo',
        ]);
      }
      if (stepType === 'endor-action') {
        // For Endor Labs actions, we may need to pass a Microsoft user token based on feature flag and frontend input
        userToken = await microsoftAuthApi.getAccessToken([
          'api://b1a34801-b490-4078-aa8a-ca27d2b0f2f6/agent.start',
        ]);
      }
      // Use bulk API if multiple issues (resource-grouped)
      const isBulkExecution = issueIds && issueIds.length > 1;
      const executionResponse: AutoFixV2StepExecutionResponse = isBulkExecution
        ? await yardstickApi.executeAutofixV2StepBulk(
            issueIds!,
            autofixStep.handler,
            autofixStep.params || {},
            userToken, // Pass userToken for GitHub/azure actions based on issue type
          )
        : await yardstickApi.executeAutofixV2Step(issueId, {
            stepName: autofixStep.handler,
            params: autofixStep.params || {},
            userToken, // Pass userToken for GitHub/azure actions based on issue type
          });

      const newStepResult: AutofixV2Step = {
        id: `${issueId}-${autofixStep.handler}`,
        stepName: autofixStep.handler,
        stepConfig: autofixStep,
        stepInputParams: autofixStep.params || {},
        stepOutputParams: executionResponse.result?.autoFixResult?.output,
        autofixText: executionResponse.result?.autofixText,
        error_message: executionResponse.error,
        status: (() => {
          if (executionResponse.status === 'success') return 'success';
          if (executionResponse.status === 'failed') return 'failed';
          if (executionResponse.status === 'pending') return 'pending';
          return executionResponse.status;
        })(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Track success/failure result
      try {
        const resultType =
          newStepResult.status === 'success' ? 'success' : 'failure';
        const user = await getUserInfo();

        // Extract PR/Issue details from step output for tracking
        const outputParams = newStepResult.stepOutputParams || {};
        const { prUrl, prNumber, issueUrl, issueNumber, repository } =
          extractGitHubResourceDetails(outputParams);

        analytics.captureEvent(
          'insights:auto_remediation:result',
          'auto_remediation',
          {
            attributes: {
              entity: entityRef || 'unknown',
              checkId: checkId || 'unknown',
              issueId: issueId || 'unknown',
              stepType,
              resultType,
              user,
              // Include GitHub resource details for tracking adoption
              ...(prUrl && { pullRequestUrl: prUrl }),
              ...(prNumber && { pullRequestNumber: prNumber }),
              ...(issueUrl && { githubIssueUrl: issueUrl }),
              ...(issueNumber && { githubIssueNumber: issueNumber }),
              ...(repository && { repository }),
            },
          },
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('failed to capture analytic event:', error);
      }

      setState(prev => ({
        ...prev,
        executing: false,
        stepResult: newStepResult,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        executing: false,
        error:
          error instanceof Error ? error.message : 'Failed to execute autofix',
      }));
    }
  }, [
    issueIds,
    issueId,
    autofixStep,
    yardstickApi,
    githubAuthApi,
    microsoftAuthApi,
    entityRef,
    checkId,
    analytics,
    getUserInfo,
    state.stepResult,
  ]);

  // Initialize on mount
  useEffect(() => {
    fetchStepResult();
    return undefined;
  }, [fetchStepResult]);

  const completed =
    state.stepResult &&
    (state.stepResult.status === 'completed' ||
      state.stepResult.status === 'success');

  const failed = state.stepResult && state.stepResult.status === 'failed';

  const pending =
    state.stepResult &&
    (state.stepResult.status === 'pending' ||
      state.stepResult.status === 'in_progress');

  // Poll for updates when step is pending
  useEffect(() => {
    if (pending) {
      const pollInterval = setInterval(() => {
        fetchStepResult();
      }, 30000); // Poll every 30 seconds

      return () => clearInterval(pollInterval);
    }
    return undefined;
  }, [pending, fetchStepResult]);

  // Render content
  const renderContent = () => {
    // Handle failed state
    if (failed) {
      return (
        <Box mb={3}>
          <Alert severity="error" style={{ marginBottom: '16px' }}>
            Failed to {actionText.verb.toLowerCase()}{' '}
            {actionText.noun.toLowerCase()}
            {state.stepResult?.error_message && (
              <>: {state.stepResult.error_message}</>
            )}
          </Alert>
          <Typography variant="body2" color="textSecondary">
            The auto-remediation could not be applied. Click "Retry" to try
            again.
          </Typography>
        </Box>
      );
    }

    // Handle pending state
    if (pending) {
      return (
        <Box mb={3}>
          <Alert severity="info" style={{ marginBottom: '16px' }}>
            Auto-remediation is{' '}
            {state.stepResult?.status === 'pending' ? 'queued' : 'in progress'}
            ...
          </Alert>
          <Typography variant="body2" color="textSecondary">
            Please wait while the auto-remediation is being applied. This page
            will automatically update.
          </Typography>
        </Box>
      );
    }

    // PHASE 1: Before execution
    if (!completed) {
      const getDescriptionText = () => {
        if (isGitHubIssue) {
          return 'Create a GitHub issue with the below changes then assign it to GitHub Copilot to automate both implementation and pull request creation.';
        }
        if (isPullRequest) {
          return 'Create a pull request with the recommended changes to fix this issue.';
        }
        if (isEndorAction) {
          return 'Apply the Endor Action to automatically remediate this issue.';
        }
        return 'Apply the automated remediation for this issue.';
      };

      // Extract issue body from params if available
      const issueBody = autofixStep.params?.body;

      const togglePreview = () => {
        setState(prev => ({ ...prev, previewExpanded: !prev.previewExpanded }));
      };

      return (
        <Box mb={3}>
          {/* Description Text */}
          <Typography
            variant="body2"
            style={{
              marginBottom: issueBody ? '16px' : '16px',
              fontSize: '14px',
              lineHeight: '22px',
              color: theme.palette.text.secondary,
            }}
          >
            {isGitHubIssue ? (
              <>
                <strong style={{ color: theme.palette.text.primary }}>
                  Create a GitHub issue
                </strong>{' '}
                with the below changes{' '}
                {!copilotRequested ? 'then' : 'it will be'}{' '}
                <strong style={{ color: theme.palette.text.primary }}>
                  {!copilotRequested ? 'assign it' : 'automatically assigned'}{' '}
                  to GitHub Copilot
                </strong>{' '}
                to automate both implementation and pull request creation.
              </>
            ) : (
              getDescriptionText()
            )}
          </Typography>

          {/* Non-GitHub actions (e.g. Endor upgrade) render their body as plain
              markdown text, matching the post-execution result styling — no
              boxed/collapsible preview. */}
          {issueBody && !isGitHubIssue && (
            <ReactMarkdown>{issueBody}</ReactMarkdown>
          )}

          {/* GitHub issue preview: a collapsible boxed preview because the body
              is long-form issue markdown. */}
          {issueBody && isGitHubIssue && (
            <>
              <Box
                style={{
                  backgroundColor:
                    theme.palette.type === 'dark' ? '#1A1D23' : '#FFFFFF',
                  border: `1px solid ${
                    theme.palette.type === 'dark' ? '#30363D' : '#D0D7DE'
                  }`,
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                {/* Header with title and show more/less button */}
                <Box
                  style={{
                    padding: '12px 16px',
                    backgroundColor:
                      theme.palette.type === 'dark' ? '#161B22' : '#F6F8FA',
                    borderBottom: `1px solid ${
                      theme.palette.type === 'dark' ? '#30363D' : '#D0D7DE'
                    }`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="h6"
                    style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    GitHub issue preview
                  </Typography>
                  {/* Only show toggle button if not forced to expand */}
                  {!forceExpandPreview && (
                    <Box
                      onClick={togglePreview}
                      style={{
                        padding: '4px 12px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        color: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {state.previewExpanded ? '▼ Show less' : '▶ Show more'}
                    </Box>
                  )}
                </Box>
                {/* Issue Body */}
                <Box
                  style={{
                    padding: '16px',
                    maxHeight:
                      forceExpandPreview || state.previewExpanded
                        ? 'none'
                        : '250px',
                    overflow:
                      forceExpandPreview || state.previewExpanded
                        ? 'visible'
                        : 'hidden',
                    position: 'relative',
                    transition: 'max-height 0.3s ease',
                  }}
                >
                  <ReactMarkdown
                    components={{
                      h1: ({ node, ...h1Props }) => (
                        <Box
                          component="h1"
                          {...h1Props}
                          style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            marginTop: '16px',
                            marginBottom: '12px',
                            color: theme.palette.text.primary,
                          }}
                        />
                      ),
                      h2: ({ node, ...h2Props }) => (
                        <Box
                          component="h2"
                          {...h2Props}
                          style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            marginTop: '16px',
                            marginBottom: '10px',
                            color: theme.palette.text.primary,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            paddingBottom: '6px',
                          }}
                        />
                      ),
                      h3: ({ node, ...h3Props }) => (
                        <Box
                          component="h3"
                          {...h3Props}
                          style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            marginTop: '12px',
                            marginBottom: '8px',
                            color: theme.palette.text.primary,
                          }}
                        />
                      ),
                      p: ({ node, ...pProps }) => (
                        <Box
                          component="p"
                          {...pProps}
                          style={{
                            fontSize: '14px',
                            lineHeight: '22px',
                            marginBottom: '12px',
                            color: theme.palette.text.primary,
                          }}
                        />
                      ),
                      a: linkProps => (
                        <Link
                          href={linkProps.href}
                          component="a"
                          color="primary"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: 'none',
                            color:
                              theme.palette.type === 'dark'
                                ? '#58A6FF'
                                : '#0969DA',
                          }}
                        >
                          {linkProps.children}
                        </Link>
                      ),
                      ul: ({ node, ...ulProps }) => (
                        <ul
                          {...ulProps}
                          style={{
                            paddingLeft: '24px',
                            marginBottom: '12px',
                            color: theme.palette.text.primary,
                          }}
                        />
                      ),
                      ol: ({ node, ...olProps }) => (
                        <ol
                          {...olProps}
                          style={{
                            paddingLeft: '24px',
                            marginBottom: '12px',
                            color: theme.palette.text.primary,
                          }}
                        />
                      ),
                      li: ({ node, ...liProps }) => (
                        <li
                          {...liProps}
                          style={{
                            fontSize: '14px',
                            lineHeight: '22px',
                            marginBottom: '6px',
                            color: theme.palette.text.primary,
                          }}
                        />
                      ),
                      code: ({ node, inline, ...codeProps }) => (
                        <code
                          {...codeProps}
                          style={{
                            backgroundColor:
                              theme.palette.type === 'dark'
                                ? 'rgba(110,118,129,0.4)'
                                : 'rgba(175,184,193,0.2)',
                            color: theme.palette.text.primary,
                            padding: inline ? '2px 6px' : '0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontFamily:
                              'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
                            wordBreak: 'break-all',
                            whiteSpace: 'pre-wrap',
                          }}
                        />
                      ),
                      pre: ({ node, ...preProps }) => (
                        <pre
                          {...preProps}
                          style={{
                            backgroundColor:
                              theme.palette.type === 'dark'
                                ? '#161B22'
                                : '#F6F8FA',
                            border: `1px solid ${
                              theme.palette.type === 'dark'
                                ? '#30363D'
                                : '#D0D7DE'
                            }`,
                            color: theme.palette.text.primary,
                            padding: '16px',
                            borderRadius: '6px',
                            overflowX: 'auto',
                            fontSize: '13px',
                            fontFamily:
                              'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
                            margin: '12px 0',
                            maxWidth: '100%',
                          }}
                        />
                      ),
                      blockquote: ({ node, ...blockquoteProps }) => (
                        <blockquote
                          {...blockquoteProps}
                          style={{
                            borderLeft: `4px solid ${
                              theme.palette.type === 'dark'
                                ? '#30363D'
                                : '#D0D7DE'
                            }`,
                            paddingLeft: '16px',
                            marginLeft: '0',
                            marginBottom: '12px',
                            color: theme.palette.text.secondary,
                            fontStyle: 'italic',
                          }}
                        />
                      ),
                    }}
                  >
                    {issueBody}
                  </ReactMarkdown>

                  {/* Fade effect when collapsed */}
                  {!state.previewExpanded && (
                    <Box
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '80px',
                        background:
                          theme.palette.type === 'dark'
                            ? 'linear-gradient(to bottom, rgba(26, 29, 35, 0) 0%, rgba(26, 29, 35, 0.8) 50%, rgba(26, 29, 35, 1) 100%)'
                            : 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 1) 100%)',
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      );
    }

    // Parse markdown to extract GitHub issue details
    const parseGitHubIssueDetails = (markdown: string) => {
      const details: {
        repository?: string;
        url?: string;
        title?: string;
        state?: string;
        copilotAgentAssigned?: boolean;
      } = {};

      // Extract repository
      const repoMatch = markdown.match(/\*\*Repository\*\*:\s*`([^`]+)`/);
      if (repoMatch) details.repository = repoMatch[1];

      // Extract URL
      const urlMatch = markdown.match(/\*\*URL\*\*:\s*\[([^\]]+)\]\(([^)]+)\)/);
      if (urlMatch) details.url = urlMatch[2];

      // Extract title
      const titleMatch = markdown.match(/\*\*Title\*\*:\s*(.+?)(?:\n|$)/);
      if (titleMatch) details.title = titleMatch[1].trim();

      // Extract state
      const stateMatch = markdown.match(/\*\*State\*\*:\s*(\w+)/);
      if (stateMatch) details.state = stateMatch[1];

      const assigned = /Issue created and assigned to `copilot`/.test(markdown);
      details.copilotAgentAssigned = assigned;

      return details;
    };

    const issueDetails =
      isGitHubIssue && state.stepResult?.autofixText
        ? parseGitHubIssueDetails(state.stepResult.autofixText)
        : null;

    return (
      <Box mb={3}>
        {/* Action Required Section */}
        {!issueDetails?.copilotAgentAssigned ? (
          <Box
            style={{
              backgroundColor:
                theme.palette.type === 'dark' ? '#1A3A1A' : '#E8F5E9',
              border: `1px solid ${
                theme.palette.type === 'dark' ? '#2E7D32' : '#4CAF50'
              }`,
              borderRadius: '4px',
              padding: '16px',
              marginBottom: '16px',
            }}
          >
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 600,
                marginBottom: '8px',
                color: theme.palette.text.primary,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              ⚡ Action required
            </Typography>
            <Typography
              variant="body2"
              style={{
                color: theme.palette.text.primary,
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              {issueDetails?.url && !issueDetails?.copilotAgentAssigned ? (
                <>
                  <Link
                    href={issueDetails.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Review the generated issue
                  </Link>
                  , then assign it to GitHub Copilot to automate both
                  implementation and pull request creation.
                </>
              ) : (
                actionText.actionRequired
              )}
            </Typography>
          </Box>
        ) : (
          <></>
        )}

        {/* Details Section */}
        {!state.stepResult?.autofixText && (
          <Typography
            variant="body2"
            style={{
              color: theme.palette.text.secondary,
              fontSize: '14px',
            }}
          >
            No additional details available.
          </Typography>
        )}
        {state.stepResult?.autofixText && isGitHubIssue && (
          <Box
            style={{
              backgroundColor:
                theme.palette.type === 'dark' ? '#1E1E1E' : '#F5F5F5',
              borderRadius: '4px',
              padding: '16px',
            }}
          >
            <Typography
              variant="subtitle1"
              style={{
                marginBottom: '16px',
                fontWeight: 600,
                fontSize: '15px',
                color: theme.palette.text.primary,
              }}
            >
              Github issue details
            </Typography>

            {issueDetails && (
              <Box
                component="ul"
                style={{
                  margin: 0,
                  paddingLeft: '20px',
                  listStyle: 'disc',
                }}
              >
                {issueDetails.repository && (
                  <Box component="li" style={{ marginBottom: '10px' }}>
                    <Typography
                      variant="body2"
                      component="span"
                      style={{
                        fontSize: '14px',
                        color: theme.palette.text.primary,
                      }}
                    >
                      <strong>Repository:</strong> {issueDetails.repository}
                    </Typography>
                  </Box>
                )}

                {issueDetails.url && (
                  <Box component="li" style={{ marginBottom: '10px' }}>
                    <Typography
                      variant="body2"
                      component="span"
                      style={{
                        fontSize: '14px',
                        color: theme.palette.text.primary,
                      }}
                    >
                      <strong>URL:</strong>{' '}
                      <Link
                        href={issueDetails.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '14px',
                          textDecoration: 'none',
                        }}
                      >
                        {issueDetails.url}
                      </Link>
                    </Typography>
                  </Box>
                )}

                {issueDetails.title && (
                  <Box component="li" style={{ marginBottom: '10px' }}>
                    <Typography
                      variant="body2"
                      component="span"
                      style={{
                        fontSize: '14px',
                        color: theme.palette.text.primary,
                      }}
                    >
                      <strong>Title:</strong> {issueDetails.title}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
        {state.stepResult?.autofixText && !isGitHubIssue && (
          <ReactMarkdown>{state.stepResult.autofixText}</ReactMarkdown>
        )}
      </Box>
    );
  };

  // Render action buttons
  const renderButtons = () => {
    if (completed) {
      return (
        <Box mt={2} display="flex" style={{ gap: '8px' }}>
          {/* Endor actions can be re-run after a successful apply, so keep the
              action button available. GitHub issue/PR flows remain one-shot. */}
          {isEndorAction && (
            <ActionButton
              onClick={executeStep}
              disabled={state.executing || !!pending}
            >
              {state.executing
                ? 'Executing...'
                : `${actionText.verb} ${actionText.noun} Again`}
            </ActionButton>
          )}
          {onClose && (
            <ActionButton onClick={onClose} variant="outline">
              Close
            </ActionButton>
          )}
        </Box>
      );
    }

    const getButtonText = () => {
      if (failed) {
        return 'Retry';
      }
      return `${actionText.verb} ${actionText.noun}`;
    };

    return (
      <Box mt={2} display="flex" style={{ gap: '8px' }}>
        <ActionButton
          onClick={executeStep}
          disabled={state.executing || !!pending}
        >
          {state.executing ? 'Executing...' : getButtonText()}
        </ActionButton>
        {onClose && (
          <ActionButton onClick={onClose} variant="outline">
            Cancel
          </ActionButton>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      {state.loading && (
        <Box mb={2}>
          <LoadingState text="Loading auto-remediation status..." />
        </Box>
      )}

      {!state.loading && state.fetchError && (
        <Box mb={2}>
          <Alert severity="error">{state.fetchError}</Alert>
          {onClose && (
            <Box mt={2}>
              <ActionButton onClick={onClose} variant="outline">
                Close
              </ActionButton>
            </Box>
          )}
        </Box>
      )}

      {!state.loading && !state.fetchError && (
        <>
          {state.executing && (
            <Box mb={2}>
              <LoadingState text="Executing auto-remediation..." />
            </Box>
          )}

          {renderContent()}
          {renderButtons()}
        </>
      )}
    </Box>
  );
};
